import * as React from "react";
import {
  useExpanded,
  useFilters,
  useGroupBy,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import ReactTablePagination from "./reactTablePagination";

import { NumberFilterInputProps } from "../../interfaces";
import { ENTER, SPACE } from "../../utils/keyNames";

const DEFAULT_PAGE_SIZE = 100;

function fromHeaderHelp(e) {
  return !!(e.target as HTMLElement).closest?.('.header-help')
}

function columnClassName(isSorted, isSortedDesc) {
  const defaultClassName = "rt-th -cursor-pointer";
  if (!isSorted) {
    return defaultClassName;
  }
  if (isSortedDesc) {
    return `${defaultClassName} -sort-desc`;
  }
  return `${defaultClassName} -sort-asc`;
}

export const TextFilter = ({ column, setFilter }) => {
  const handleChange = React.useCallback((event) => setFilter(column.id, event.target.value), [column.id, setFilter]);

  return (
    <input
      aria-label="text-filter"
      onChange={handleChange}
      style={{ width: "100%" }}
      value={column.filterValue}
    />
  );
};

export const NumberFilterInput = ({
  handleChange,
  label,
  column,
}: NumberFilterInputProps) => {
  const handleInputChange = React.useCallback((e) => handleChange(column.id, e.target.value), [column.id, handleChange]);

  return (
    <div style={{ display: "flex" }}>
      <input
        aria-label={label}
        onChange={handleInputChange}
        placeholder="0-5, >1, <1"
        style={{ marginRight: "0.5rem", width: "100px" }}
        type="text"
        value={column.filterValue || ""}
      />
    </div>
  );
};

export const expanderColumn = {
  Cell: function ExpanderCell({ row }) {
    return (
      <div
        {...row.getToggleRowExpandedProps()}
        className="rt-td rt-expandable p-0"
        title="Click here to see more information"
      >
        <div className={`rt-expander ${row.isExpanded ? "-open" : ""}`}>•</div>
      </div>
    );
  },
  Header: "",
  className: "text-center",
  id: "expander",
  resizable: false,
};

interface ReactTableProps {
  SubComponent?: any;
  className?: string;
  columns: any[];
  currentPage?: number;
  data: any[];
  defaultGroupBy?: string;
  defaultPageSize?: number;
  defaultSorted?: string | { id: string; desc: boolean }[];
  disableSortBy?: boolean;
  // Header clicks cycle between ascending and descending, never back to unsorted
  disableSortRemove?: boolean;
  // Identifies rows across renders, so reordered data keeps its DOM rows
  // (react-table defaults to the row's index)
  getRowId?: (row: any, index: number) => string;
  // Extra attributes for a row's <tr>, such as aria-hidden on decorative rows
  getRowProps?: (row: any) => Record<string, unknown>;
  filterable?: boolean;
  manualFilters?: boolean;
  manualPagination?: boolean;
  manualSortBy?: boolean;
  manualPageCount?: boolean;
  minRows?: number;
  onFiltersChange?: (filters: []) => void;
  onPageChange?: (pageIndex: number) => void;
  onSortedChange?: (sortBy: string) => void;
  showPagination?: boolean;
  showPaginationBottom?: boolean;
  // Render a <colgroup> from the leaf column widths and use table-layout: fixed. Needed for grouped
  // (super-header) columns so colSpan cells align to their columns and per-column widths are honored.
  useColgroup?: boolean;
}

export const ReactTable = ({
  SubComponent,
  className,
  columns,
  currentPage,
  data,
  defaultGroupBy,
  defaultPageSize,
  defaultSorted,
  disableSortBy,
  disableSortRemove,
  filterable,
  getRowId,
  getRowProps,
  manualFilters,
  manualPagination,
  manualPageCount,
  manualSortBy,
  onFiltersChange,
  onPageChange,
  onSortedChange,
  showPaginationBottom,
  useColgroup,
}: ReactTableProps) => {
  const defaultColumn = {
    width: "min-content",
  };

  const optionsForTable = {
    autoResetSortBy: false,
    columns,
    data,
    defaultColumn,
    disableSortBy,
    disableSortRemove,
    ...(getRowId ? { getRowId } : {}),
    filters: [],
    groupBy: defaultGroupBy || [],
    initialState: {
      filters: [],
      groupBy: defaultGroupBy || [],
      pageIndex: currentPage || 0,
      pageSize: defaultPageSize || data.length || DEFAULT_PAGE_SIZE,
      sortBy: defaultSorted || [],
    },
    manualFilters,
    manualPagination,
    manualSortBy,
    SubComponent,
  }

  // see discussion here: https://github.com/TanStack/table/issues/2321
  if (manualPagination) {
    optionsForTable.pageCount = manualPageCount
  }

  const {
    getTableBodyProps,
    getTableProps,
    gotoPage,
    headerGroups,
    visibleColumns,
    page,
    canNextPage,
    canPreviousPage,
    pageCount,
    prepareRow,
    state: { filters, pageIndex, sortBy },
  } = useTable(
    optionsForTable,
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  );

  React.useEffect(() => {
    if (manualSortBy && onSortedChange) {
      onSortedChange(sortBy);
    }
  }, [sortBy]);

  React.useEffect(() => {
    if (manualPagination && onPageChange) {
      onPageChange(pageIndex);
    }
  }, [pageIndex]);

  React.useEffect(() => {
    if (manualFilters && onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters]);

  // Lets a header's help icon open its tooltip without sorting
  function sortToggleProps(column) {
    const props = column.getSortByToggleProps({
      "aria-label": column.ariaLabel || column.render("Header"),
      className: "focus-on-light",
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.key !== ENTER && e.key !== SPACE) { return }
        e.preventDefault();
        if (!fromHeaderHelp(e)) { column.toggleSortBy(); }
      },
    })

    return { ...props, onClick: (e) => { if (!fromHeaderHelp(e)) { props.onClick?.(e) } } }
  }

  function handleRowClick(row) {
    if (!row.original?.link) { return }

    window.location = row.original.link
  }

  return (
    <div className={`${className} ReactTable`}>
      <table className="rt-table" {...getTableProps(useColgroup ? { style: { tableLayout: "fixed", width: "100%" } } : {})}>
        {useColgroup && (
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
          </colgroup>
        )}
        <thead className="rt-thead -header">
          {headerGroups.map((headerGroup) => (
            <tr className="rt-tr" key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  aria-label={column.ariaLabel || column.Header}
                  className={columnClassName(column.isSorted, column.isSortedDesc)}
                  key={column.id}
                  scope="col"
                  {...column.getHeaderProps({
                    style: {
                      maxWidth: column.maxWidth,
                      minWidth: column.minWidth,
                      width: column.width,
                    },
                  })}
                >
                  <div
                    {...sortToggleProps(column)}
                    className="sortable-header focus-on-light"
                  >
                    {column.render("Header")}
                  </div>
                  <div className="column-filter">
                    {filterable && column.canFilter ? column.render("Filter") : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="rt-tbody" {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <tr {...row.getRowProps({ ...(getRowProps ? getRowProps(row) : {}), onClick: () => { handleRowClick(row) } })} className={`rt-tr-group rt-tr ${row.original?.link ? 'show-pointer' : ''}`}>
                  {row.cells.map((cell) => (
                    <td
                      className="rt-td"
                      key={cell.column.id}
                      {...cell.getCellProps({
                        style: {
                          maxWidth: cell.column.maxWidth,
                          minWidth: cell.column.minWidth,
                          width: cell.column.width,
                          ...cell.column.style,
                        },
                      })}
                    >
                      {cell.isAggregated ? cell.render("Aggregated") : cell.render("Cell")}
                    </td>
                  ))}
                </tr>
                {row.isExpanded && row.original && SubComponent
                  ? SubComponent(row)
                  : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {showPaginationBottom && (
        <ReactTablePagination
          canNext={canNextPage}
          canPrevious={canPreviousPage}
          onPageChange={gotoPage}
          page={pageIndex}
          pages={pageCount}
        />
      )}
    </div>
  );
};
